// OutputPresentationCard.js - renders a Present tool result.
// Created live from agentic.output.present events and on history reload from role:'presentation' messages.
import { ComponentBase } from "../../core/ComponentBase.js";
import { marked } from "https://cdn.jsdelivr.net/npm/marked/lib/marked.esm.js";

const FILE_ICONS = {
    py:'bi-filetype-py', js:'bi-filetype-js', ts:'bi-filetype-tsx',
    sh:'bi-terminal', json:'bi-filetype-json', md:'bi-file-richtext',
    csv:'bi-filetype-csv', txt:'bi-file-text', log:'bi-journal-text',
    pdf:'bi-file-pdf', png:'bi-file-image', jpg:'bi-file-image',
    jpeg:'bi-file-image', zip:'bi-file-zip', html:'bi-filetype-html',
    xlsx:'bi-filetype-xlsx',
};
function fileIcon(name) {
    const ext = (name.split('.').pop() || '').toLowerCase();
    return FILE_ICONS[ext] || 'bi-file-earmark';
}

export class OutputPresentationCard extends ComponentBase {
    static styles = `
        .opc-card {
            border: 1px solid var(--bs-border-color-translucent);
            border-radius: var(--ab-radius-lg); overflow: hidden;
            margin: 8px 0; max-width: 560px;
            background: var(--bs-body-bg);
            box-shadow: var(--ab-shadow-xs);
        }
        .opc-card.type-result  { border-left: 4px solid var(--bs-success); }
        .opc-card.type-progress{ border-left: 4px solid var(--bs-primary); }
        .opc-card.type-warning { border-left: 4px solid var(--bs-warning); }
        .opc-card.type-info    { border-left: 4px solid var(--bs-info); }

        .opc-header {
            padding: 10px 14px 6px;
            display: flex; align-items: center; gap: 8px;
        }
        .opc-title { font-weight: 700; font-size: var(--ab-text-md); flex: 1; }
        .opc-body  { padding: 0 14px 6px; font-size: var(--ab-text-sm); line-height: 1.6; }
        .opc-body p { margin-bottom: 0.4rem; }
        .opc-files {
            border-top: 1px solid var(--bs-border-color-translucent);
            padding: 8px 14px;
        }
        .opc-file {
            display: flex; align-items: center; gap: 8px;
            padding: 5px 0; font-size: var(--ab-text-sm);
            border-bottom: 1px solid var(--bs-border-color-translucent);
        }
        .opc-file:last-child { border-bottom: none; }
        .opc-file-info { flex: 1; min-width: 0; }
        .opc-file-label { font-weight: 500; display: block; }
        .opc-file-desc  { color: var(--bs-secondary-color); font-size: var(--ab-text-xs); }
        .opc-file-actions { display: flex; gap: 4px; flex-shrink: 0; }
    `;

    constructor(config) {
        super(config);
        this.title    = config.title    || 'Output';
        this.summary  = config.summary  || '';
        this.type     = config.type     || 'result';
        this.files    = config.files    || [];
        this.threadId = config.threadId;
        this.projectId= config.projectId;
        this.spaceId  = config.spaceId;
        this.agentName= config.agentName || null;
        this.applyStyles();
    }

    render(parentEl) {
        const typeIcons = { result:'bi-check-circle-fill text-success', progress:'bi-hourglass-split text-primary', warning:'bi-exclamation-triangle-fill text-warning', info:'bi-info-circle-fill text-info' };
        const icon = typeIcons[this.type] || typeIcons.result;

        this.container = document.createElement('div');
        this.container.className = `opc-card type-${this.type}`;

        const filesHtml = this.files.length ? `
            <div class="opc-files">
                ${this.files.map(f => `
                <div class="opc-file">
                    <i class="bi ${fileIcon(f.label || f.path)} text-secondary" style="font-size:var(--ab-text-md);flex-shrink:0"></i>
                    <div class="opc-file-info">
                        <span class="opc-file-label">${this.escapeHtml(f.label || f.path)}</span>
                        ${f.description ? `<span class="opc-file-desc">${this.escapeHtml(f.description)}</span>` : ''}
                    </div>
                    <div class="opc-file-actions">
                        <button class="btn btn-sm btn-outline-secondary py-0 px-1 opc-preview-btn"
                            title="Preview" data-path="${this.escapeHtml(f.path)}" data-name="${this.escapeHtml(f.path.split('/').pop())}">
                            <i class="bi bi-eye"></i>
                        </button>
                        <button class="btn btn-sm btn-outline-primary py-0 px-1 opc-download-btn"
                            title="Download" data-path="${this.escapeHtml(f.path)}">
                            <i class="bi bi-download"></i>
                        </button>
                    </div>
                </div>`).join('')}
            </div>` : '';

        this.container.innerHTML = `
            <div class="opc-header">
                <i class="bi ${icon}" style="font-size:var(--ab-text-md);flex-shrink:0"></i>
                <span class="opc-title">${this.escapeHtml(this.title)}</span>
            </div>
            ${this.summary ? `<div class="opc-body">${marked.parse(this.summary)}</div>` : ''}
            ${filesHtml}
        `;

        // Wire preview buttons
        this.container.querySelectorAll('.opc-preview-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.appClient.events.fireLocal('previewAgentFile', {
                    threadId:  this.threadId,
                    agentName: this.agentName,
                    spaceId:   this.spaceId,
                    filePath:  btn.dataset.path,
                    fileName:  btn.dataset.name,
                    projectId: this.projectId,
                });
            });
        });

        // Wire download buttons
        this.container.querySelectorAll('.opc-download-btn').forEach(btn => {
            btn.addEventListener('click', async () => {
                try {
                    const res = await this.appClient.rpcClient.call(
                        'ThreadRpc.getAgentFileUrl',
                        { threadId: this.threadId, spaceId: this.spaceId, filePath: btn.dataset.path },
                        { projectId: this.projectId }
                    );
                    if (res?.url) {
                        const a = document.createElement('a');
                        a.href = res.url; a.download = btn.dataset.path.split('/').pop(); a.target = '_blank';
                        document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    }
                } catch (e) {
                    this.appClient.notifier?.error('Could not get download URL');
                }
            });
        });

        parentEl.appendChild(this.container);
    }
}
